import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import { createMessageTemplate, fetchMessageConfig, fetchMessageLogs, fetchMessageTemplates, updateMessageConfig, updateMessageTemplate, type MessageConfig, type MessageConfigSavePayload, type MessageTemplate, type MessageTemplateSavePayload } from '@/api/message'

export function useMessageCenter(options: {
  isMessageLogsCenter: ComputedRef<boolean>
  isMessageTemplatesCenter: ComputedRef<boolean>
  isMessageConfigCenter: ComputedRef<boolean>
}) {
  const loadingMessageLogs = ref(false)
  const loadingMessageTemplates = ref(false)
  const loadingMessageConfig = ref(false)
  const messageTemplateEditorVisible = ref(false)
  const messageTemplateSubmitting = ref(false)
  const messageConfigSubmitting = ref(false)
  const messageLogPage = ref<Awaited<ReturnType<typeof fetchMessageLogs>>>()
  const messageTemplates = ref<MessageTemplate[]>([])
  const messageConfigRows = ref<MessageConfig[]>([])
  const messageLogFilters = reactive({ page: 1, pageSize: 10 })
  const messageTemplateForm = reactive<MessageTemplateSavePayload>({
    id: undefined,
    code: '',
    scene: 'ORDER',
    wechatTemplateId: '',
    title: '',
    content: '',
    enabled: true,
  })

  const isEditingMessageTemplate = computed(() => Boolean(messageTemplateForm.id))
  const messageLogTotalPages = computed(() => !messageLogPage.value ? 1 : Math.max(1, Math.ceil(messageLogPage.value.total / messageLogPage.value.pageSize)))
  const messageLogStats = computed(() => {
    const list = messageLogPage.value?.list ?? []
    return [
      { label: '消息记录', value: String(messageLogPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '当前页成功', value: String(list.filter((item) => item.status === 'SUCCESS').length), sub: '发送成功', tone: 'success' as const },
      { label: '当前页失败', value: String(list.filter((item) => item.status === 'FAIL').length), sub: '发送失败', tone: 'error' as const },
      { label: '消息场景', value: String(new Set(list.map((item) => item.scene).filter(Boolean)).size), sub: '当前页覆盖', tone: 'warning' as const },
    ]
  })
  const messageTemplateStats = computed(() => {
    return [
      { label: '模板数量', value: String(messageTemplates.value.length), sub: '实时查询', tone: 'brand' as const },
      { label: '启用模板', value: String(messageTemplates.value.filter((item) => item.status === 'ENABLED').length), sub: '当前生效', tone: 'success' as const },
      { label: '停用模板', value: String(messageTemplates.value.filter((item) => item.status === 'DISABLED').length), sub: '待启用', tone: 'warning' as const },
      { label: '模板场景', value: String(new Set(messageTemplates.value.map((item) => item.scene).filter(Boolean)).size), sub: '配置覆盖', tone: 'brand' as const },
    ]
  })
  const messageConfigStats = computed(() => {
    return [
      { label: '配置数量', value: String(messageConfigRows.value.length), sub: '按场景维护', tone: 'brand' as const },
      { label: '已启用', value: String(messageConfigRows.value.filter((item) => item.enabled).length), sub: '当前生效', tone: 'success' as const },
      { label: '已停用', value: String(messageConfigRows.value.filter((item) => item.enabled === false).length), sub: '待开启', tone: 'warning' as const },
      { label: '发送渠道', value: String(new Set(messageConfigRows.value.map((item) => item.channel).filter(Boolean)).size), sub: '配置渠道数', tone: 'brand' as const },
    ]
  })

  async function loadMessageLogs(): Promise<void> {
    if (!options.isMessageLogsCenter.value) return
    loadingMessageLogs.value = true
    try {
      messageLogPage.value = await fetchMessageLogs({
        page: messageLogFilters.page,
        pageSize: messageLogFilters.pageSize,
      })
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '消息记录加载失败')
    } finally {
      loadingMessageLogs.value = false
    }
  }

  async function loadMessageTemplates(): Promise<void> {
    if (!options.isMessageTemplatesCenter.value) return
    loadingMessageTemplates.value = true
    try {
      messageTemplates.value = await fetchMessageTemplates()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '消息模板加载失败')
    } finally {
      loadingMessageTemplates.value = false
    }
  }

  async function loadMessageConfig(): Promise<void> {
    if (!options.isMessageConfigCenter.value) return
    loadingMessageConfig.value = true
    try {
      messageConfigRows.value = await fetchMessageConfig()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '消息配置加载失败')
    } finally {
      loadingMessageConfig.value = false
    }
  }

  async function changeMessageLogPage(page: number): Promise<void> {
    messageLogFilters.page = page
    await loadMessageLogs()
  }

  function openMessageTemplateEditor(item?: MessageTemplate): void {
    Object.assign(messageTemplateForm, {
      id: item?.id,
      code: item?.code || '',
      scene: item?.scene || 'ORDER',
      wechatTemplateId: '',
      title: item?.title || '',
      content: item?.content || '',
      enabled: item?.status !== 'DISABLED',
    })
    messageTemplateEditorVisible.value = true
  }

  async function submitMessageTemplate(): Promise<void> {
    const code = messageTemplateForm.code.trim()
    const content = messageTemplateForm.content.trim()
    if (!code || !content) {
      ElMessage.warning('请填写模板编码和模板内容')
      return
    }
    messageTemplateSubmitting.value = true
    try {
      const payload: MessageTemplateSavePayload = {
        id: messageTemplateForm.id,
        code,
        scene: messageTemplateForm.scene?.trim() || undefined,
        wechatTemplateId: messageTemplateForm.wechatTemplateId?.trim() || undefined,
        title: messageTemplateForm.title?.trim() || undefined,
        content,
        enabled: messageTemplateForm.enabled ?? true,
      }
      if (isEditingMessageTemplate.value) {
        await updateMessageTemplate(payload)
        ElMessage.success('消息模板已更新')
      } else {
        delete payload.id
        await createMessageTemplate(payload)
        ElMessage.success('消息模板已创建')
      }
      messageTemplateEditorVisible.value = false
      await loadMessageTemplates()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '消息模板保存失败')
    } finally {
      messageTemplateSubmitting.value = false
    }
  }

  async function toggleMessageConfig(item: MessageConfig): Promise<void> {
    messageConfigSubmitting.value = true
    try {
      const payload: MessageConfigSavePayload = {
        id: item.id,
        scene: item.scene,
        enabled: !(item.enabled ?? false),
        channel: item.channel || undefined,
      }
      await updateMessageConfig(payload)
      ElMessage.success('消息配置已更新')
      await loadMessageConfig()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '消息配置更新失败')
    } finally {
      messageConfigSubmitting.value = false
    }
  }

  return {
    loadingMessageLogs,
    loadingMessageTemplates,
    loadingMessageConfig,
    messageTemplateEditorVisible,
    messageTemplateSubmitting,
    messageConfigSubmitting,
    messageLogPage,
    messageTemplates,
    messageConfigRows,
    messageTemplateForm,
    messageLogTotalPages,
    messageLogStats,
    messageTemplateStats,
    messageConfigStats,
    loadMessageLogs,
    loadMessageTemplates,
    loadMessageConfig,
    changeMessageLogPage,
    openMessageTemplateEditor,
    submitMessageTemplate,
    toggleMessageConfig,
  }
}
